import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Button, Container } from 'reactstrap';

import AccountModalLink from '../../components/account/AccountModalLink';
import ContentPopups from '../../components/ad/ContentPopups';
import MainBanner from '../../components/ad/MainBanner';
import HelperNavigation from '../../components/common/HelperNavigation';
import PageTitle from '../../components/common/PageTitle';
import ScrollReset from '../../components/common/ScrollReset';
import CanonicalLink from '../../components/head/CanonicalLink';
import SEOTitle from '../../components/head/SEOTitle';
import GenericLayout from '../../components/layout/GenericLayout';
import Page from '../../components/layout/Page';
import MainProductList from '../../components/product-list/MainProductList';
import useShouldShowBanner from '../../hooks/banner/useShouldShowBanner';
import globalTranslations from '../../i18n/globalTranslations';
import { modelOf } from '../../prop-types';
import RouteService from '../../services/RouteService';
import AccountStore from '../../store/AccountStore';
import AdStore from '../../store/AdStore';
import ConfigStore from '../../store/ConfigStore';
import SectionStore from '../../store/SectionStore';
import AccountModalTab from '../../types/AccountModalTab';
import AdPages from '../../types/AdPages';
import AdZones from '../../types/AdZones';
import CommonPage from '../../types/CommonPage';
import Paths from '../../types/Paths';
import ProductSortOrderBy from '../../types/ProductSortOrderBy';
import SortOrder from '../../types/SortOrder';
import AlternateLink from "../../components/head/AlternateLink";

const CustomerProductsPage = ({
  accountStore,
  adStore,
  configStore,
  sectionStore,
  intl,
  routeService,
}) => {
  useEffect(() => {
    if (useShouldShowBanner({ adStore, page: AdPages.CUSTOMER_PRODUCTS })) {
      loadAds();
    }
  }, []);

  const loadAds = () => {
    adStore.loadAds(getSearchParams());
  };

  const getBreadcrumbsPath = () => {
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.customerProductsTitle),
        url: Paths.CustomerProducts,
      },
    ]);
  };

  const getSearchParams = () => ({
    bannerPage: AdPages.CUSTOMER_PRODUCTS,
  });

  const renderLogIn = () => (
    <AccountModalLink tab={AccountModalTab.LOGIN}>
      <Button tag="span" color="primary">
        <FormattedMessage {...globalTranslations.logInSentence} />
      </Button>
    </AccountModalLink>
  );

  const listId = 'customer_products_list';
  const listName = 'Customer Products List';

  const contentAllowed = accountStore.loggedIn && accountStore.isRetailer;

  const fixedParams = {
    orderBy: ProductSortOrderBy.CATEGORY,
    order: SortOrder.ASC,
    hasCustomerPrice: 1,
  };
  if (sectionStore.activeSection) {
    fixedParams.sections = [sectionStore.activeSection.id];
  }

  const mainBanner = useShouldShowBanner({
    adStore,
    page: AdPages.CUSTOMER_PRODUCTS,
    zone: AdZones.MAIN_BANNER,
  }) ? (
    <MainBanner searchParams={getSearchParams()} />
  ) : null;
  const mainBannerWithinContent =
    configStore.banner.mainBanner.withinContent &&
    !configStore.banner.mainBanner.fullWidth;

  return (
    <Page name={CommonPage.CUSTOMER_PRODUCTS} className="CustomerProductsPage">
      <ScrollReset />
      <SEOTitle
        title={{
          ...globalTranslations.customerProductsTitle,
        }}
      />
      <CanonicalLink path={routeService.getPath(Paths.CustomerProducts)} />
      {!mainBannerWithinContent && mainBanner}
      <Container className="CustomerProductsPage__container">
        <GenericLayout
          bannerSearchParams={
            useShouldShowBanner({
              adStore,
              page: AdPages.CUSTOMER_PRODUCTS,
              zone: AdZones.SIDEBAR_BANNER,
            })
              ? getSearchParams()
              : null
          }
          content={
            <>
              {mainBannerWithinContent && mainBanner}
              <HelperNavigation breadcrumbsPath={getBreadcrumbsPath()} />
              <PageTitle>
                <FormattedMessage
                  {...globalTranslations.customerProductsTitle}
                />
              </PageTitle>
              {contentAllowed && (
                <MainProductList
                  fixedParams={fixedParams}
                  listId={listId}
                  name={listName}
                  showSort={false}
                  showCategoryHeaders={true}
                />
              )}
              {!contentAllowed && !accountStore.loggedIn && renderLogIn()}
              {useShouldShowBanner({
                adStore,
                page: AdPages.CUSTOMER_PRODUCTS,
                zone: AdZones.POPUP_CONTENT,
              }) && <ContentPopups searchParams={getSearchParams()} />}
            </>
          }
        />
      </Container>
    </Page>
  );
};

CustomerProductsPage.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  adStore: modelOf(AdStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  sectionStore: modelOf(SectionStore).isRequired,
  intl: intlShape.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
};

export default injectIntl(
  inject(
    'accountStore',
    'adStore',
    'configStore',
    'sectionStore',
    'routeService'
  )(observer(CustomerProductsPage))
);
